import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { MapPin, Loader2, X, Map, Navigation } from 'lucide-react';
import { Input } from './Input';
import placesService from '../../services/placesService';
import MapPinPicker from './MapPinPicker';

/**
 * LocationAutocomplete Component
 * Google Places autocomplete input with dropdown suggestions
 *
 * @param {Object} props
 * @param {string} props.value - Current input value
 * @param {function} props.onChange - Callback when input changes
 * @param {function} props.onLocationSelect - Callback when location is selected with full details
 * @param {string} props.placeholder - Input placeholder
 * @param {string} props.country - Country code filter (e.g., 'in' for India)
 * @param {boolean} props.required - Whether field is required
 * @param {string} props.className - Additional CSS classes
 * @param {boolean} props.showMapPicker - Show "Pick on Map" button (default: false)
 * @param {string} props.mapPickerTitle - Title for the map picker modal
 * @param {string} props.pinColor - Color of the map pin
 * @param {boolean} props.showCurrentLocation - Show "Detect Location" button (default: false)
 * @param {boolean} props.useFullAddress - Use full formatted address instead of place name (default: false)
 * @param {React.ReactNode} props.extraActions - Extra small actions shown beside "Pick on Map"
 * @param {string} props.suggestionsPlacement - 'above' | 'below' (default: 'below') - open suggestions above or below the input
 */
const LocationAutocomplete = ({
  value = '',
  onChange,
  onLocationSelect,
  placeholder = 'Search for a location...',
  country = null, // null = global search, or pass country code like 'in', 'us', etc.
  required = false,
  className = '',
  showMapPicker = false,
  mapPickerTitle = 'Select Location',
  pinColor = '#3B82F6',
  showCurrentLocation = false,
  showSelectedStatus = true,
  useFullAddress = false,
  suggestionsPlacement = 'below',
  extraActions = null
}) => {
  const [searchText, setSearchText] = useState(value);
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [isMapPickerOpen, setIsMapPickerOpen] = useState(false);
  const [detectingLocation, setDetectingLocation] = useState(false);
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);
  const isUserTypingRef = useRef(false);
  const [dropdownRect, setDropdownRect] = useState(null);

  // Update search text when value prop changes
  useEffect(() => {
    if (value !== searchText) {
      setSearchText(value);
    }
  }, [value]);

  // Debounced autocomplete search
  useEffect(() => {
    if (searchText.length < 2) {
      setSuggestions([]);
      setShowDropdown(false);
      return;
    }

    // Don't fetch if we have a selected location with the same text AND user is not typing.
    // Once location is selected, suggestions should not keep appearing on focus or re-render.
    const isTyping = isUserTypingRef.current;
    const textMatchesSelection = selectedLocation && searchText.trim() && (
      searchText.trim() === (selectedLocation.address || '').trim() ||
      searchText.trim() === (selectedLocation.name || '').trim()
    );
    if (textMatchesSelection && !isTyping) {
      setSuggestions([]);
      setShowDropdown(false);
      return;
    }

    const timer = setTimeout(async () => {
      // Reset typing flag after debounce completes
      // At this point, if user was typing, we've fetched suggestions
      isUserTypingRef.current = false;
      
      setLoading(true);
      try {
        const response = await placesService.getAutocomplete(searchText, { country });

        if (response.success && response.data) {
          setSuggestions(response.data);
          setShowDropdown(true);
        } else {
          setSuggestions([]);
          // Still show dropdown to indicate search was performed (will show "No results" message)
          if (searchText.length >= 2) {
            setShowDropdown(true);
          }
        }
      } catch (error) {
        console.error('Autocomplete error:', error);
        setSuggestions([]);
      } finally {
        setLoading(false);
      }
    }, 300); // 300ms debounce

    return () => clearTimeout(timer);
  }, [searchText, country, selectedLocation]);

  // Measure dropdown position when open (for portal rendering so suggestions are always visible)
  const updateDropdownRect = useCallback(() => {
    if (dropdownRef.current && showDropdown) {
      const rect = dropdownRef.current.getBoundingClientRect();
      setDropdownRect({
        top: rect.top,
        bottom: rect.bottom,
        left: rect.left,
        width: rect.width
      });
    } else {
      setDropdownRect(null);
    }
  }, [showDropdown]);

  useEffect(() => {
    updateDropdownRect();
    if (!showDropdown) return;
    const onScrollOrResize = () => updateDropdownRect();
    window.addEventListener('scroll', onScrollOrResize, true);
    window.addEventListener('resize', onScrollOrResize);
    return () => {
      window.removeEventListener('scroll', onScrollOrResize, true);
      window.removeEventListener('resize', onScrollOrResize);
    };
  }, [showDropdown, updateDropdownRect, suggestions.length, searchText]);

  // Close dropdown when clicking outside (input/container or portal dropdown)
  useEffect(() => {
    const handleClickOutside = (event) => {
      const clickedInsideInput = dropdownRef.current && dropdownRef.current.contains(event.target);
      const clickedInsidePortal = event.target.closest('[data-location-autocomplete-portal]');
      if (!clickedInsideInput && !clickedInsidePortal) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setSearchText(newValue);
    // Mark that user is actively typing (not selecting from dropdown)
    isUserTypingRef.current = true;
    // Clear selected location when user is typing/editing (not selecting from dropdown)
    // This ensures suggestions can appear again after erasing text
    setSelectedLocation(null);
    // Clear suggestions dropdown temporarily while typing
    // It will be repopulated by the useEffect when debounce completes
    if (onChange) {
      onChange(e);
    }
  };

  const handleSelectPlace = async (suggestion) => {
    try {
      setLoading(true);
      // User is no longer typing - they selected from dropdown
      isUserTypingRef.current = false;

      // Get full place details with coordinates
      const response = await placesService.getPlaceDetails(suggestion.placeId);

      if (response.success && response.data) {
        const place = response.data;

        // Use full formatted address if useFullAddress is true, otherwise use place name
        const displayValue = useFullAddress && place.formattedAddress 
          ? place.formattedAddress 
          : place.name;

        // Update local state
        setSearchText(displayValue);
        setSelectedLocation({
          name: place.name,
          address: place.formattedAddress,
          latitude: place.location.latitude,
          longitude: place.location.longitude,
          placeId: place.placeId
        });
        setShowDropdown(false);

        // Notify parent components
        if (onChange) {
          onChange({
            target: {
              value: displayValue
            }
          });
        }

        if (onLocationSelect) {
          onLocationSelect({
            name: place.name,
            address: place.formattedAddress,
            latitude: place.location.latitude,
            longitude: place.location.longitude,
            placeId: place.placeId
          });
        }
      }
    } catch (error) {
      console.error('Error fetching place details:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleClear = () => {
    setSearchText('');
    setSelectedLocation(null);
    setSuggestions([]);
    setShowDropdown(false);
    // User cleared the field, mark as typing so suggestions can appear when they type again
    isUserTypingRef.current = true;
    if (onChange) {
      onChange({ target: { value: '' } });
    }
    if (onLocationSelect) {
      onLocationSelect(null);
    }
    inputRef.current?.focus();
  };

  // Handle location selection from map picker
  const handleMapLocationSelect = (location) => {
    if (location) {
      // Use address as primary value, fallback to name if address is not available
      // Ensure we never use an empty value - fallback to coordinates if needed
      const locationAddress = location.address || '';
      const locationName = location.name || locationAddress || '';
      // When useFullAddress is true, prefer full address; otherwise use name or address
      const displayValue = useFullAddress && locationAddress
        ? locationAddress
        : (locationAddress || locationName || 
          (location.latitude && location.longitude 
            ? `${location.latitude.toFixed(4)}, ${location.longitude.toFixed(4)}` 
            : ''));
      
      // Only proceed if we have a valid display value
      if (!displayValue) {
        console.error('MapPinPicker: No valid address or coordinates provided');
        return;
      }
      
      setSearchText(displayValue);
      setSelectedLocation({
        name: locationName || displayValue,
        address: locationAddress || displayValue,
        latitude: location.latitude,
        longitude: location.longitude,
        placeId: null // Map picker doesn't have placeId
      });
      setShowDropdown(false);
      isUserTypingRef.current = false;

      // Notify parent components - use address as the display value to ensure field is not empty
      // Call onLocationSelect first so parent can set coordinates, then onChange to update the input field
      if (onLocationSelect) {
        onLocationSelect({
          name: locationName || displayValue,
          address: locationAddress || displayValue,
          latitude: location.latitude,
          longitude: location.longitude,
          placeId: null
        });
      }

      // Then update the input field value - use address to ensure it's not empty
      if (onChange) {
        onChange({
          target: {
            value: displayValue
          }
        });
      }
    }
  };

  // Handle detecting current location using browser geolocation. The first
  // getCurrentPosition call on a page can fail/time out while the OS location
  // provider is still warming up (common on Windows/Chrome with
  // enableHighAccuracy) even though a follow-up call moments later succeeds
  // — so on failure we retry once with lower-accuracy (faster, WiFi/IP-based)
  // settings before surfacing an error to the user.
  const handleDetectCurrentLocation = async () => {
    if (!navigator.geolocation) {
      alert('Geolocation is not supported by your browser');
      return;
    }

    setDetectingLocation(true);

    const onPositionResolved = async (position) => {
        const { latitude, longitude } = position.coords;

        try {
          // Use reverse geocode to get address from coordinates
          const response = await placesService.reverseGeocode(latitude, longitude);

          if (response.success && response.data) {
            const addressData = response.data;
            // Get first 3 parts of the address for the name
            const addressParts = addressData.formattedAddress.split(',').map(part => part.trim());
            const locationName = addressParts.slice(0, 3).join(', ');
            // Use full formatted address if useFullAddress is true, otherwise use shortened name
            const displayValue = useFullAddress && addressData.formattedAddress
              ? addressData.formattedAddress
              : locationName;

            setSearchText(displayValue);
            setSelectedLocation({
              name: locationName,
              address: addressData.formattedAddress,
              latitude: latitude,
              longitude: longitude,
              placeId: addressData.placeId || null
            });
            setShowDropdown(false);
            isUserTypingRef.current = false;

            // Notify parent components
            if (onChange) {
              onChange({
                target: {
                  value: displayValue
                }
              });
            }

            if (onLocationSelect) {
              onLocationSelect({
                name: locationName,
                address: addressData.formattedAddress,
                latitude: latitude,
                longitude: longitude,
                placeId: addressData.placeId || null
              });
            }
          } else {
            // Fallback: use coordinates as location name
            const locationName = `${latitude.toFixed(4)}, ${longitude.toFixed(4)}`;
            setSearchText(locationName);
            setSelectedLocation({
              name: locationName,
              address: locationName,
              latitude: latitude,
              longitude: longitude,
              placeId: null
            });
            setShowDropdown(false);

            if (onChange) {
              onChange({ target: { value: locationName } });
            }
            if (onLocationSelect) {
              onLocationSelect({
                name: locationName,
                address: locationName,
                latitude: latitude,
                longitude: longitude,
                placeId: null
              });
            }
          }
        } catch (error) {
          console.error('Error reverse geocoding:', error);
          // Fallback: use coordinates
          const locationName = `${latitude.toFixed(4)}, ${longitude.toFixed(4)}`;
          setSearchText(locationName);
          setSelectedLocation({
            name: locationName,
            address: locationName,
            latitude: latitude,
            longitude: longitude,
            placeId: null
          });

          if (onChange) {
            onChange({ target: { value: locationName } });
          }
          if (onLocationSelect) {
            onLocationSelect({
              name: locationName,
              address: locationName,
              latitude: latitude,
              longitude: longitude,
              placeId: null
            });
          }
        } finally {
          setDetectingLocation(false);
        }
    };

    const onPositionFailed = (error) => {
      setDetectingLocation(false);
      let errorMessage = 'Unable to detect your location';
      switch (error.code) {
        case error.PERMISSION_DENIED:
          errorMessage = 'Location permission denied. Please enable location access in your browser settings.';
          break;
        case error.POSITION_UNAVAILABLE:
          errorMessage = 'Location information is unavailable.';
          break;
        case error.TIMEOUT:
          errorMessage = 'Location request timed out.';
          break;
      }
      alert(errorMessage);
    };

    navigator.geolocation.getCurrentPosition(
      onPositionResolved,
      (error) => {
        // Don't retry after an explicit permission denial — retrying won't help.
        if (error.code === error.PERMISSION_DENIED) {
          onPositionFailed(error);
          return;
        }
        navigator.geolocation.getCurrentPosition(
          onPositionResolved,
          onPositionFailed,
          { enableHighAccuracy: false, timeout: 8000, maximumAge: 60000 }
        );
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0
      }
    );
  };

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      <div className="relative">
        <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
          <MapPin className="h-4 w-4" />
        </div>

        <Input
          ref={inputRef}
          type="text"
          value={searchText}
          onChange={handleInputChange}
          onFocus={() => {
            // Once a location is selected, do NOT show suggestions again on focus.
            // Only show suggestions when user actually types/edits the field.
            const textMatchesSelection = selectedLocation && searchText && (
              searchText.trim() === (selectedLocation.address || '').trim() ||
              searchText.trim() === (selectedLocation.name || '').trim()
            );
            if (textMatchesSelection) {
              setShowDropdown(false);
              return;
            }
            // User has edited text or cleared - allow suggestions when they focus with existing suggestions
            if (suggestions.length > 0) {
              setShowDropdown(true);
            }
          }}
          placeholder={placeholder}
          required={required}
          className="!pl-10 !pr-10"
        />

        {searchText && (
          <button
            type="button"
            onClick={handleClear}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
          >
            <X className="h-4 w-4" />
          </button>
        )}

        {loading && (
          <div className="absolute right-10 top-1/2 -translate-y-1/2 text-gray-400">
            <Loader2 className="h-4 w-4 animate-spin" />
          </div>
        )}
      </div>

      {/* Selected location indicator and action buttons */}
      <div className={`flex items-center ${showSelectedStatus && selectedLocation ? 'justify-between' : 'justify-end'} mt-1`}>
        {showSelectedStatus && selectedLocation && (
          <div className="text-xs text-green-600 flex items-center gap-1">
            <div className="h-2 w-2 bg-green-600 rounded-full"></div>
            Location selected
          </div>
        )}

        <div className="flex items-center gap-3">
          {showCurrentLocation && (
            <button
              type="button"
              onClick={handleDetectCurrentLocation}
              disabled={detectingLocation}
              className="text-xs text-green-600 hover:text-green-700 flex items-center gap-1 font-medium transition-colors disabled:opacity-50"
            >
              {detectingLocation ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Navigation className="h-3.5 w-3.5" />
              )}
              {detectingLocation ? 'Detecting...' : 'Use My Location'}
            </button>
          )}

          {showMapPicker && (
            <button
              type="button"
              onClick={() => setIsMapPickerOpen(true)}
              className="text-xs text-blue-600 hover:text-blue-700 flex items-center gap-1 font-medium transition-colors"
            >
              <Map className="h-3.5 w-3.5" />
              Pick on Map
            </button>
          )}

          {extraActions}
        </div>
      </div>

      {/* Dropdown suggestions - rendered in portal so they're always visible above modals/overflow */}
      {showDropdown && dropdownRect && createPortal(
        <div
          data-location-autocomplete-portal
          className="bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto"
          style={{
            position: 'fixed',
            ...(suggestionsPlacement === 'above'
              ? { bottom: window.innerHeight - dropdownRect.top + 4, left: dropdownRect.left, width: dropdownRect.width }
              : { top: dropdownRect.bottom + 4, left: dropdownRect.left, width: dropdownRect.width }
            ),
            zIndex: 10001
          }}
        >
          {suggestions.length > 0 ? (
            <ul className="w-full">
              {suggestions.map((suggestion) => (
                <li
                  key={suggestion.placeId}
                  onClick={() => handleSelectPlace(suggestion)}
                  className="px-4 py-3 hover:bg-gray-100 cursor-pointer border-b last:border-b-0 transition-colors"
                >
                  <div className="flex items-start gap-2">
                    <MapPin className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-gray-900 truncate">
                        {suggestion.mainText}
                      </div>
                      <div className="text-sm text-gray-500 truncate">
                        {suggestion.secondaryText}
                      </div>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          ) : !loading && searchText.length >= 2 ? (
            <div className="px-4 py-3 text-sm text-gray-500">
              No locations found. Try a different search.
            </div>
          ) : null}
        </div>,
        document.body
      )}

      {/* Map Pin Picker Modal */}
      {showMapPicker && (
        <MapPinPicker
          isOpen={isMapPickerOpen}
          onClose={() => setIsMapPickerOpen(false)}
          onConfirm={handleMapLocationSelect}
          initialLocation={null}
          title={mapPickerTitle}
          pinColor={pinColor}
        />
      )}
    </div>
  );
};

export default LocationAutocomplete;
